import { useState } from 'react';
import * as XLSX from 'xlsx';
import { Card, SectionTitle, Btn, FileUpload } from './UI';
import { ACT, ACT_ORDER, loadSvFacts } from '../lib/activities';
import { excelToISO } from '../lib/exportExcel';

const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/[−–—]/g, '-').replace(/\s+/g, ' ');
const NAME2CODE = {};
ACT_ORDER.forEach(c => { NAME2CODE[norm(ACT[c].label)] = c; NAME2CODE[c] = c; });
NAME2CODE['стажировка ме-'] = 'stm';
NAME2CODE['аудиты'] = 'au'; NAME2CODE['аудит'] = 'au';

function parse(file) {
  return new Promise((resolve, reject) => {
    const rd = new FileReader();
    rd.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: false });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1 });
        const hi = rows.findIndex(r => (r || []).some(v => norm(v) === 'св'));
        if (hi < 0) throw new Error('Не найдена строка заголовков (колонка «СВ»)');
        const h = rows[hi].map(norm);
        const col = (names) => h.findIndex(x => names.includes(x));
        const c = { sv: col(['св']), date: col(['дата']), act: col(['активность', 'тип']), cnt: col(['количество', 'кол-во']), cand: col(['кандидат']) };
        if (c.date < 0 || c.act < 0) throw new Error('Нужны колонки: СВ, Дата, Активность (и по желанию Количество, Кандидат)');
        const out = []; const bad = [];
        rows.slice(hi + 1).forEach((r, i) => {
          if (!r || !r[c.sv]) return;
          const code = NAME2CODE[norm(r[c.act])];
          const date = excelToISO(r[c.date]);
          if (!code || !date) { bad.push(`строка ${hi + i + 2}`); return; }
          const n = c.cnt >= 0 && r[c.cnt] !== undefined && r[c.cnt] !== '' ? parseInt(r[c.cnt], 10) : null;
          out.push({ sv: String(r[c.sv]).trim(), date, activity: code, cnt: Number.isFinite(n) ? n : null, candidate: c.cand >= 0 ? (r[c.cand] ? String(r[c.cand]).trim() : null) : null });
        });
        resolve({ rows: out, bad });
      } catch (err) { reject(err); }
    };
    rd.onerror = () => reject(new Error('Не удалось прочитать файл'));
    rd.readAsBinaryString(file);
  });
}

function downloadTemplate() {
  const aoa = [['СВ', 'Дата', 'Активность', 'Количество', 'Кандидат'],
    ['код СВ или ФИО', '2026-10-05', 'Перекрытие', 3, ''],
    ['код СВ или ФИО', '2026-10-06', 'Стажировка МЕ', 3, 'Иванов Иван Петрович'],
    ['код СВ или ФИО', '2026-10-07', 'Офисный день', '', '']];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'Факт активностей');
  XLSX.writeFile(wb, 'шаблон_факт_активностей_св.xlsx');
}

export default function SvFactsTab({ actorId }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [res, setRes] = useState(null);

  async function go() {
    if (!file) return;
    setBusy(true); setErr(''); setRes(null);
    try {
      const { rows, bad } = await parse(file);
      if (!rows.length) throw new Error('В файле нет распознанных строк');
      const r = await loadSvFacts(rows, actorId);
      setRes({ ...r, total: rows.length, bad });
      setFile(null);
    } catch (e) { setErr('Ошибка: ' + e.message); } finally { setBusy(false); }
  }

  return (
    <div>
      <SectionTitle>Факт активностей СВ</SectionTitle>
      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)', lineHeight: 1.5 }}>
        Колонки: <b>СВ</b> (внутренний код или ФИО как в системе), <b>Дата</b>, <b>Активность</b> (Перекрытие, Стажировка МЕ, Стажировка МЕ−, Офисный день, Аудиты СВ, Отпуск), <b>Количество</b>, <b>Кандидат</b>.
        Если для СВ и даты в файле есть записи, весь ручной факт этого СВ за этот день заменяется данными из файла. План не меняется.
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <FileUpload label="Файл факта активностей (.xlsx)" accept=".xlsx,.xls" onFile={setFile} hint="шаблон можно скачать ниже" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {err && <div style={{ fontSize: 12, color: 'var(--red)', margin: '10px 0' }}>{err}</div>}
          {res && (
            <div style={{ fontSize: 12, margin: '10px 0', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', background: '#F8FAFC' }}>
              <div style={{ color: 'var(--green)', fontWeight: 600, marginBottom: 6 }}>✓ Загружено записей: {res.inserted} из {res.total} (дней СВ: {res.days})</div>
              {res.skipped > 0 && <div style={{ color: 'var(--red)' }}>Пропущено в базе: {res.skipped}. Не найдены СВ: {(res.unmatched || []).join(', ')}</div>}
              {res.bad.length > 0 && <div style={{ color: 'var(--red)' }}>Не распознаны: {res.bad.slice(0, 10).join(', ')}{res.bad.length > 10 ? ' …' : ''}</div>}
            </div>
          )}
          <div style={{ marginTop: 10 }}><Btn size="full" onClick={go} disabled={!file || busy}>{busy ? 'Загрузка...' : 'Загрузить'}</Btn></div>
          <button onClick={downloadTemplate} style={{ marginTop: 10, width: '100%', padding: '9px 12px', fontSize: 12, fontWeight: 600, color: 'var(--accent)', background: '#fff', border: '1px solid var(--accent)', borderRadius: 8, cursor: 'pointer' }}>Скачать шаблон</button>
        </div>
      </Card>
    </div>
  );
}
